<template>
  <b-modal
      v-bind="$props"
      :id="id"
      centered
      footer-class="border-0 pt-0"
      hide-header
      no-close-on-backdrop
      no-close-on-esc
  >
    <div class="text-center py-2">
      <div class="d-flex justify-content-center mb-3">
        <img :src="AlertIcon" alt="Alert Icon" width="40" height="40" />
      </div>
      <h4>{{ cancelConfirmTitle }}</h4>
      <p class="m-0 text-muted" v-html="cancelConfirmDescription"></p>
    </div>
    <validation-observer ref="form" tag="div" class="text-start mt-3">
      <validation-provider
          v-slot="{ validated, errors }"
          :name="lodash.startCase($t('purchase_plans.modal_messages.cancel_reason_label'))"
          rules="required"
          vid="reason"
      >
        <b-form-group
            :invalid-feedback="errors[0]"
            :label="`${lodash.startCase($t('purchase_plans.modal_messages.cancel_reason_label'))}*`"
            :state="errors[0] ? false : (validated ? true : null)"
            label-for="cancel-reason"
        >
          <b-form-textarea
              id="cancel-reason"
              v-model="form.reason"
              :placeholder="$t('purchase_plans.modal_messages.cancel_reason_placeholder')"
              :state="errors[0] ? false : (validated ? true : null)"
              rows="4"
          ></b-form-textarea>
        </b-form-group>
      </validation-provider>
    </validation-observer>
    <template #modal-footer>
      <div class="d-flex gap-3 w-100">
        <b-button
            :disabled="loading"
            class="w-50"
            variant="light"
            @click="handleCancel"
        >
          {{ $t('Cancel') }}
        </b-button>
        <b-button
            :disabled="loading"
            class="w-50 m-0"
            variant="danger"
            @click="handleConfirm"
        >
          <b-spinner v-if="loading" class="me-2" small></b-spinner>
          {{ lodash.startCase($t('purchase_plans.buttons.yes_cancel')) }}
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import ConfirmationModal from '@/components/modals/confirmation-modal.vue'
import AlertIcon from '@/assets/icons/AlertIcon.svg'
import Swal from 'sweetalert2'

export default {
  name: 'purchase-plan-cancel-confirmation',
  extends: ConfirmationModal,
  props: {
    id: {default: 'purchase-plan-cancel-confirmation-modal'},
    hideHeader: {default: true},
    title: {default: ''},
  },
  data() {
    return {
      AlertIcon,
      loading: false,
      targetModel: null,
      form: {
        reason: '',
      },
    };
  },
  computed: {
    hasApprovedSteps() {
      return (this.targetModel?.approval_progress?.completed_count ?? 0) > 0
    },
    targetModelReference() {
      const model = this.targetModel
      if (!model) return ''

      if (model.name && `${model.name}`.trim() !== '') {
        return `${model.name}`.trim()
      }

      return model.id;
    },
    cancelConfirmTitle() {
      return `${this.$t('purchase_plans.modal_messages.confirm_cancel_title')}?`
    },
    cancelConfirmDescription() {
      const key = this.hasApprovedSteps
          ? 'purchase_plans.modal_messages.{something}_confirm_cancel_approved_description'
          : 'purchase_plans.modal_messages.{something}_confirm_cancel_description'
      const description = this.$t(key)
      const safeSomething = this.lodash.escape(this.targetModelReference)

      return description.replace('{something}', `<strong>${safeSomething}</strong>`)
    },
  },
  methods: {
    show(model) {
      this.targetModel = model ?? null
      this.form.reason = ''
      this.$bvModal.show(this.id)
      this.$nextTick(() => {
        this.$refs.form?.reset()
      })
    },
    handleCancel() {
      if (this.loading) return
      this.hide()
    },
    async handleConfirm() {
      if (!this.$refs.form) return

      const isValid = await this.$refs.form.validate()
      if (!isValid) return

      const targetModel = this.targetModel
      const reason = this.form.reason.trim()
      this.loading = true

      return this.$axios.post(`/v1/purchase_plans/${targetModel.id}/cancel`, {reason})
          .then(() => {
            this.hide()

            return Swal.fire({
              title: this.$t('Success') + '!',
              text: this.$t('purchase_plans.modal_messages.cancel_success_message', {something: targetModel.id}),
              type: 'success',
              confirmButtonClass: 'btn btn-success btn-fill',
              buttonsStyling: false
            }).then(() => {
              this.$emit('cancelled', targetModel.id)
            })
          })
          .catch((error) => {
            return Swal.fire({
              title: this.$t('Failed') + '!',
              text: (error.response && (error.response.data || {}).message) || error.message,
              type: 'danger',
              confirmButtonClass: 'btn btn-danger btn-fill',
              buttonsStyling: false
            })
          })
          .finally(() => {
            this.loading = false
          })
    },
  }
}
</script>

<style scoped lang="scss">
::v-deep {
  .modal-body {
    color: var(--neutral-gray-800, #454646);
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
  }

  .modal-footer {
    border: none;
  }
}
</style>
